import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import servicesDataRaw from '../data/services.json';
import contentData from '../data/content.json';
import { FaArrowUp, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';

const servicesData = servicesDataRaw;
const SERVICES_PER_PAGE = 6;
const CONTENT_PER_PAGE = 6;

function SearchResultsPage() {
    const [searchParams] = useSearchParams();
    const query = searchParams.get('q')?.toLowerCase() || '';
    const [filteredServices, setFilteredServices] = useState([]);
    const [filteredContent, setFilteredContent] = useState([]);
    // 슬라이드 인덱스
    const [servicePage, setServicePage] = useState(0);
    const [contentPage, setContentPage] = useState(0);

    useEffect(() => {
        if (query) {
            // 서비스 검색
            const services = servicesData.filter(service => {
                const id = service.id?.toLowerCase() || '';
                const description = service.description?.toLowerCase() || '';
                const category = service.category?.toLowerCase() || '';
                const summary = service.summary?.toLowerCase() || '';
                return id.includes(query) || description.includes(query) || category.includes(query) || summary.includes(query);
            });
            setFilteredServices(services);
            setServicePage(0);
            // 콘텐츠 검색
            const content = contentData.filter(item => {
                const title = item.title?.toLowerCase() || '';
                const summary = item.summary?.toLowerCase() || '';
                const type = item.type?.toLowerCase() || '';
                return title.includes(query) || summary.includes(query) || type.includes(query);
            });
            setFilteredContent(content);
            setContentPage(0);
        } else {
            setFilteredServices([]);
            setFilteredContent([]);
            setServicePage(0);
            setContentPage(0);
        }
    }, [query]);

    // 슬라이드 페이징
    const serviceTotalPages = Math.ceil(filteredServices.length / SERVICES_PER_PAGE);
    const contentTotalPages = Math.ceil(filteredContent.length / CONTENT_PER_PAGE);
    const currentServices = filteredServices.slice(servicePage * SERVICES_PER_PAGE, (servicePage + 1) * SERVICES_PER_PAGE);
    const currentContent = filteredContent.slice(contentPage * CONTENT_PER_PAGE, (contentPage + 1) * CONTENT_PER_PAGE);

    // 맨 위로 스크롤 함수
    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <>
            <Helmet>
                {/* ───────── TITLE ───────── */}
                <title>
                    {query
                        ? `‘${query}’ 검색 결과 | AI 사이트 허브`
                        : 'AI 서비스·콘텐츠 통합 검색 | AI 사이트 허브'}
                </title>

                {/* ───────── DESCRIPTION ───────── */}
                <meta
                    name="description"
                    content={
                        query
                            ? `‘${query}’(와)과 관련된 생성형 AI 서비스·뉴스·영상 결과를 한곳에서 비교해 보세요.`
                            : '키워드로 생성형 AI 서비스, 뉴스, 영상 등 다양한 콘텐츠를 검색할 수 있습니다.'
                    }
                />

                {/* ───────── KEYWORDS ───────── */}
                <meta
                    name="keywords"
                    content={
                        query
                            ? `${query}, AI 서비스, 생성형 AI, AI 툴, ChatGPT, Midjourney, AI 뉴스`
                            : 'AI 검색, 생성형 AI 서비스, AI 툴, AI 콘텐츠, ChatGPT 대안'
                    }
                />

                {/* ───────── 크롤링 & 정규화 ───────── */}
                <meta name="robots" content="index,follow" />
                <link rel="canonical" href={window.location.href} />

                {/* ───────── Open Graph / Twitter ───────── */}
                <meta property="og:type" content="website" />
                <meta
                    property="og:title"
                    content={
                        query ? `‘${query}’ 검색 결과 | AI 사이트 허브` : 'AI 통합 검색 | AI 사이트 허브'
                    }
                />
                <meta
                    property="og:description"
                    content="생성형 AI 서비스와 관련 콘텐츠를 빠르게 찾아보세요."
                />
                <meta property="og:image" content="https://www.withaihub.com/TitleImg.png" />
                <meta property="og:url" content={window.location.href} />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={query ? `‘${query}’ 검색 결과` : 'AI 통합 검색'} />
                <meta name="twitter:description" content="AI 서비스·뉴스·영상 등 다양한 생성형 AI 콘텐츠를 검색해 보세요." />
                <meta name="twitter:image" content="https://www.withaihub.com/TitleImg.png" />

                {/* ───────── JSON-LD (SearchResultsPage) ───────── */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'SearchResultsPage',
                        name: 'AI 사이트 허브 검색',
                        description: query
                            ? `‘${query}’에 대한 검색 결과 페이지`
                            : '생성형 AI 관련 콘텐츠 검색 페이지',
                        mainEntity: {
                            '@type': 'SearchAction',
                            target: window.location.href,
                            'query-input': 'required name=query',
                        },
                    })}
                </script>
            </Helmet>
            <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-slate-900">
                <div className="fixed top-0 left-0 w-full z-50">

                    <Header showFitViewButton={false} />
                </div>

                <main className="flex-grow w-full max-w-4xl mx-auto py-8 px-4 pt-36">
                    <h1 className="text-3xl font-bold text-gray-800 dark:text-white mb-6">
                        '<span className="text-blue-500">{searchParams.get('q')}</span>' 검색 결과
                    </h1>

                    {/* 서비스 검색 결과 - 슬라이드 */}
                    <section>
                        <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-4">관련 AI 서비스 ({filteredServices.length})</h2>
                        <div className="relative">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {currentServices.map(service => (
                                    <Link to={`/details/${service.id}`} key={service.id} className="block p-4 bg-white dark:bg-gray-800 rounded-lg shadow hover:bg-gray-100 dark:hover:bg-gray-700 transition h-48 flex flex-col justify-between">
                                        <div className="flex items-center mb-2">
                                            <img src={`/Images/Logos/${service.id.replace(/ /g, '')}.png`} alt={service.id} className="w-10 h-10 object-contain rounded mr-3 bg-white border dark:border-gray-700" />
                                            <div>
                                                <h3 className="font-bold text-lg text-gray-900 dark:text-white">{service.name || service.id}</h3>
                                                <p className="text-xs text-gray-500">{service.category}</p>
                                            </div>
                                        </div>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{service.summary || service.description}</p>
                                        {service.tags && (
                                            <div className="flex flex-wrap gap-1 mt-2">
                                                {service.tags.map((t, i) => <span key={i} className="text-xs bg-green-100 text-green-800 rounded px-2 py-1">{t}</span>)}
                                            </div>
                                        )}
                                    </Link>
                                ))}
                            </div>
                            {/* 좌우 슬라이드 버튼 */}
                            {serviceTotalPages > 1 && (
                                <>
                                    <button onClick={() => setServicePage(p => p === 0 ? serviceTotalPages - 1 : p - 1)}
                                        className="absolute -left-12 top-1/2 -translate-y-1/2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl w-10 h-12 text-2xl flex items-center justify-center shadow hover:bg-gray-100 dark:hover:bg-gray-600 z-10">
                                        <FaChevronLeft />
                                    </button>
                                    <button onClick={() => setServicePage(p => p === serviceTotalPages - 1 ? 0 : p + 1)}
                                        className="absolute -right-12 top-1/2 -translate-y-1/2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl w-10 h-12 text-2xl flex items-center justify-center shadow hover:bg-gray-100 dark:hover:bg-gray-600 z-10">
                                        <FaChevronRight />
                                    </button>
                                    {/* 페이지 표시 */}
                                    <div className="absolute left-1/2 -translate-x-1/2 bottom-[-2.5rem] text-sm text-gray-500 dark:text-gray-400 font-semibold">
                                        {servicePage + 1} / {serviceTotalPages}
                                    </div>
                                </>
                            )}
                        </div>
                    </section>

                    {/* 콘텐츠 검색 결과 - 슬라이드 (수정된 부분) */}
                    {/* Highlight: mb-20 클래스를 추가하여 푸터와의 간격을 확보합니다. */}
                    <section className="mt-12 mb-20">
                        <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-4">관련 콘텐츠 ({filteredContent.length})</h2>
                        <div className="relative">
                            <div className="grid grid-cols-3 gap-4">
                                {currentContent.map(item => (
                                    <Link to={`/content/${item.contentId}`} key={item.contentId} className="block bg-white dark:bg-gray-800 rounded-lg shadow hover:bg-gray-100 dark:hover:bg-gray-700 transition aspect-square flex flex-col justify-between p-4">
                                        <div>
                                            <img src={item.thumbnail || '/Images/Logos/Base.jpg'} alt={item.title} className="w-full h-32 object-cover rounded mb-2" />
                                            <h3 className="font-bold text-base text-gray-900 dark:text-white mb-1 line-clamp-2">{item.title}</h3>
                                        </div>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{(item.summary || '').slice(0, 50)}{(item.summary && item.summary.length > 50) ? '...' : ''}</p>
                                    </Link>
                                ))}
                            </div>
                            {/* 좌우 슬라이드 버튼 */}
                            {contentTotalPages > 1 && (
                                <>
                                    <button onClick={() => setContentPage(p => p === 0 ? contentTotalPages - 1 : p - 1)}
                                        className="absolute -left-12 top-1/2 -translate-y-1/2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl w-10 h-12 text-2xl flex items-center justify-center shadow hover:bg-gray-100 dark:hover:bg-gray-600 z-10">
                                        <FaChevronLeft />
                                    </button>
                                    <button onClick={() => setContentPage(p => p === contentTotalPages - 1 ? 0 : p + 1)}
                                        className="absolute -right-12 top-1/2 -translate-y-1/2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl w-10 h-12 text-2xl flex items-center justify-center shadow hover:bg-gray-100 dark:hover:bg-gray-600 z-10">
                                        <FaChevronRight />
                                    </button>
                                    {/* 페이지 표시 */}
                                    <div className="absolute left-1/2 -translate-x-1/2 bottom-[-2.5rem] text-sm text-gray-500 dark:text-gray-400 font-semibold">
                                        {contentPage + 1} / {contentTotalPages}
                                    </div>
                                </>
                            )}
                        </div>
                    </section>
                </main>
                <Footer />
            </div>

        </>
    );
}

export default SearchResultsPage;